/**
 * 2FA enrollment page.
 *
 * Admins land here when the backend returns TWO_FACTOR_REQUIRED.
 * The page walks through three steps:
 *   1. Call `authClient.twoFactor.enable()` to get a TOTP URI + QR data
 *   2. User scans the QR in their authenticator app
 *   3. User enters the 6-digit code → `authClient.twoFactor.verifyTotp()`
 *
 * On success, the user is redirected back to the dashboard — the next
 * API call will pass the 2FA check in the auth middleware because the
 * user's `twoFactorEnabled` flag is now true.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authClient } from '../lib/auth'

export const Route = createFileRoute('/setup-2fa')({
  component: Setup2FAPage,
})

function Setup2FAPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'password' | 'scan' | 'verify'>('password')
  const [password, setPassword] = useState('')
  const [totpURI, setTotpURI] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const result = await authClient.twoFactor.enable({ password })
      if (result.error) {
        setError(result.error.message ?? 'Failed to enable 2FA')
        return
      }
      const data = result.data as {
        totpURI?: string
        backupCodes?: string[]
      } | null
      setTotpURI(data?.totpURI ?? null)
      setBackupCodes(data?.backupCodes ?? null)
      setStep('scan')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length !== 6) return
    setLoading(true)
    setError(null)
    try {
      const result = await authClient.twoFactor.verifyTotp({ code })
      if (result.error) {
        setError(result.error.message ?? 'Invalid code')
        return
      }
      toast.success('Two-factor authentication is now active')
      void navigate({ to: '/' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Set up two-factor authentication</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 'password' && (
            <form onSubmit={handleEnable} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Admin accounts are required to have two-factor authentication enabled. You'll need
                an authenticator app (Google Authenticator, Authy, 1Password, etc.) on your phone.
                Enter your current password to get started.
              </p>
              <div>
                <Label htmlFor="current-pw">Current password</Label>
                <Input
                  id="current-pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={loading || !password} className="w-full">
                {loading ? 'Setting up…' : 'Continue'}
              </Button>
            </form>
          )}

          {step === 'scan' && (
            <>
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app, then click "Next" to enter the
                6-digit code.
              </p>
              {totpURI && (
                <div className="rounded-md border p-4 flex flex-col items-center gap-3">
                  <QRCodeSVG value={totpURI} size={200} />
                  <details className="text-center">
                    <summary className="text-[10px] text-muted-foreground cursor-pointer">
                      Can't scan? Show manual entry URI
                    </summary>
                    <p className="mt-1 text-xs font-mono break-all select-all">{totpURI}</p>
                  </details>
                </div>
              )}
              {backupCodes && backupCodes.length > 0 && (
                <div className="rounded-md border p-3 space-y-1">
                  <Label className="text-xs font-semibold">
                    Backup codes (save these somewhere safe)
                  </Label>
                  <div className="grid grid-cols-2 gap-1 font-mono text-xs">
                    {backupCodes.map((c) => (
                      <span key={c} className="select-all">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={() => setStep('verify')} className="w-full">
                I've scanned it — next
              </Button>
            </>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app to confirm setup.
              </p>
              <div>
                <Label htmlFor="totp-code">Code</Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  autoFocus
                  className="text-center text-2xl tracking-widest font-mono"
                />
              </div>
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full">
                {loading ? 'Verifying…' : 'Verify & activate'}
              </Button>
            </form>
          )}

          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
